import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { generateThumbnailFromDesign } from '../../utils/generateThumbnailFromDesign';
import { trackEvent } from '../../utils/analytics';
import { XMarkIcon, ClockIcon } from '@heroicons/react/24/outline';

interface LocalDesign {
  canvasData: {
    frontCanvasJSON: any;
    backCanvasJSON: any;
  };
  timestamp: string;
  id: string;
}

interface CloudDesign {
  id: string;
  version: number;
  updatedAt: string;
  frontPreviewUrl: string;
  backPreviewUrl: string;
  title: string;
  frontSide: any;
  backSide: any;
}

interface ConflictResolutionModalProps {
  local: LocalDesign;
  cloud: CloudDesign;
  onResolve: (choice: 'local' | 'cloud' | 'compare') => void;
  onClose: () => void;
}

/**
 * Conflict Resolution Modal
 * 
 * Shows side-by-side comparison of local and cloud designs
 * when a conflict is detected after login
 */
export const ConflictResolutionModal: React.FC<ConflictResolutionModalProps> = ({
  local,
  cloud,
  onResolve,
  onClose,
}) => {
  const [localThumb, setLocalThumb] = useState<string>('');
  const [cloudThumb, setCloudThumb] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadThumbnails = async () => {
      try {
        // Generate local thumbnail
        const localPreview = await generateThumbnailFromDesign(
          local.canvasData.frontCanvasJSON, 
          256
        );
        setLocalThumb(localPreview);

        // Use cloud thumbnail (already generated)
        setCloudThumb(cloud.frontPreviewUrl || cloud.backPreviewUrl || '');
      } catch (error) {
        console.error('Failed to generate thumbnails:', error);
        toast.error('Failed to load previews');
      } finally {
        setLoading(false);
      }
    };

    loadThumbnails();
    
    // Track conflict shown
    trackEvent('conflict.shown', {
      localTimestamp: local.timestamp,
      cloudTimestamp: cloud.updatedAt,
      cloudVersion: cloud.version,
    });
  }, [local, cloud]);

  const handleResolve = (choice: 'local' | 'cloud' | 'compare') => {
    trackEvent('conflict.resolved', { choice });
    onResolve(choice);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Design Conflict Detected</h2>
            <p className="text-sm text-gray-600 mt-1">
              Choose which version to keep
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Local Version */}
              <div className="border-2 border-gray-300 rounded-lg p-4 hover:border-purple-500 transition-colors">
                <div className="flex items-center gap-2 mb-3">
                  <ClockIcon className="h-5 w-5 text-gray-500" />
                  <h3 className="font-semibold text-lg text-gray-900">Your Device</h3>
                </div>
                
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-3">
                  {localThumb ? (
                    <img 
                      src={localThumb} 
                      alt="Local design" 
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      No preview
                    </div>
                  )}
                </div>
                
                <p className="text-sm text-gray-600 mb-4">
                  Last edited: {new Date(local.timestamp).toLocaleString()}
                </p>
                
                <button
                  onClick={() => handleResolve('local')}
                  className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors font-medium"
                >
                  Keep Local Version
                </button>
              </div>

              {/* Cloud Version */}
              <div className="border-2 border-gray-300 rounded-lg p-4 hover:border-blue-500 transition-colors">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                  </svg>
                  <h3 className="font-semibold text-lg text-gray-900">Cloud (Newer)</h3>
                </div>
                
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-3">
                  {cloudThumb ? (
                    <img 
                      src={cloudThumb} 
                      alt="Cloud design" 
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      No preview
                    </div>
                  )}
                </div>
                
                <p className="text-sm text-gray-600 mb-1">
                  Last edited: {new Date(cloud.updatedAt).toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  Version {cloud.version}
                </p>
                
                <button
                  onClick={() => handleResolve('cloud')}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                >
                  Use Cloud Version
                </button>
              </div>
            </div>
          )}

          {/* Compare Button */}
          {!loading && (
            <div className="mt-6 text-center">
              <button
                onClick={() => handleResolve('compare')}
                className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
              >
                Compare Side-by-Side
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};





